import { IAntDProTableRequest } from "../../../general/type/general.types";
import { apiRequest, downloadFile } from "../../../services/api";
import {
  ILeaveRequest,
  IApiResponseGetLeaves,
  IUpdateLeaveStatusRequest,
  IApiResponseGetUserLeaveBalanceByType,
} from "../type/leave";

export const getLeaves = async (
  tableRequest: IAntDProTableRequest
): Promise<IApiResponseGetLeaves> => {
  const response = await apiRequest("GET", "/leaves", tableRequest);
  return response.data;
};

export const getUserLeaves = async (
  userId: string,
  tableRequest: IAntDProTableRequest
): Promise<IApiResponseGetLeaves> => {
  const response = await apiRequest(
    "GET",
    `/leaves/user-leaves/${userId}`,
    tableRequest
  );
  return response.data;
};

export const createLeave = async (data: ILeaveRequest, file?: File) => {
  if (file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("leaveType", data.leaveType);
    formData.append("startDate", data.startDate.toISOString());
    formData.append("endDate", data.endDate.toISOString());
    if (data.totalDays) {
      formData.append("totalDays", data.totalDays.toString());
    }
    if (data.reason) {
      formData.append("reason", data.reason);
    }

    const response = await apiRequest("POST", `/leaves`, formData, true);
    return response.data;
  } else {
    const payload = {
      leaveType: data.leaveType,
      startDate: data.startDate,
      endDate: data.endDate,
      ...(data.totalDays && { totalDays: data.totalDays }),
      ...(data.reason && { reason: data.reason }),
    };
    const response = await apiRequest<ILeaveRequest>(
      "POST",
      "/leaves",
      payload
    );
    return response.data;
  }
};

export const getLeaveById = async (id: string | undefined) => {
  const response = await apiRequest<IApiResponseGetLeaves>(
    "GET",
    `/leaves/${id}`
  );
  return response.data;
};

export const editLeave = async (
  id: string,
  data: ILeaveRequest,
  file?: File
) => {
  if (file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("leaveType", data.leaveType);
    formData.append("startDate", data.startDate.toISOString());
    formData.append("endDate", data.endDate.toISOString());
    if (data.totalDays) {
      formData.append("totalDays", data.totalDays.toString());
    }
    if (data.reason) {
      formData.append("reason", data.reason);
    }

    const response = await apiRequest("PATCH", `/leaves/${id}`, formData, true);
    return response.data;
  } else {
    const payload = {
      ...(data.leaveType && { leaveType: data.leaveType }),
      ...(data.startDate && { startDate: data.startDate }),
      ...(data.endDate && { endDate: data.endDate }),
      ...(data.totalDays && { totalDays: data.totalDays }),
      ...(data.reason && { reason: data.reason }),
    };

    const response = await apiRequest<ILeaveRequest>(
      "PATCH",
      `/leaves/${id}`,
      payload
    );
    return response.data;
  }
};

export const updateLeaveStatus = async (
  id: string,
  data: IUpdateLeaveStatusRequest
) => {
  const payload = {
    status: data.status,
  };
  const response = await apiRequest<IUpdateLeaveStatusRequest>(
    "PATCH",
    `/leaves/${id}/status`,
    payload
  );
  return response.data;
};

export const archiveLeave = async (id: string) => {
  const response = await apiRequest("PATCH", `/leaves/${id}/archive`);
  return response.data;
};

export const getUserLeavesBalanceByType = async (
  leaveType: string
): Promise<IApiResponseGetUserLeaveBalanceByType> => {
  const response = await apiRequest(
    "GET",
    `/leaves/user-leave-balance/${leaveType}`
  );
  return response.data;
};

export const downloadLeaveFileFromS3 = async (
  leaveId: string,
  relativePath: string
) => {
  try {
    // Use the standardized downloadFile function to get the blob
    const blob = await downloadFile(
      `/leaves/${leaveId}/download?filePath=${encodeURIComponent(relativePath)}`
    );

    // Get the filename from the relative path
    const fileName = relativePath.split("/").pop() || "downloaded-file";

    // Create download URL
    const downloadUrl = window.URL.createObjectURL(blob);

    // *** Try to open in new tab for supported file types (PDF, images) *** //
    const isPdf = blob.type === "application/pdf";
    const isImage = blob.type.startsWith("image/");

    if (isPdf || isImage) {
      // Open in new tab for supported file types
      const newTab = window.open(downloadUrl, "_blank");

      // Fallback to download if opening in new tab fails
      if (!newTab || newTab.closed || typeof newTab.closed === "undefined") {
        // Browser blocked opening new tab, fall back to download
        triggerDownload(downloadUrl, fileName);
      } else {
        // Revoke the blob URL when the parent page is closed/refreshed
        // This prevents the blob URL from being reused after closing the parent tab
        window.addEventListener(
          "beforeunload",
          () => {
            window.URL.revokeObjectURL(downloadUrl);
          },
          { once: true }
        );
      }
    } else {
      // For other file types, use regular download
      triggerDownload(downloadUrl, fileName);
    }
    return true;
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

// Helper function to trigger file download
const triggerDownload = (url: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  // Clean up
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }, 100);
};

export const checkOverlappingLeaves = async (
  startDate: string | Date,
  endDate: string | Date,
  leaveId?: string
): Promise<{ hasOverlap: boolean }> => {
  const payload = {
    startDate,
    endDate,
    ...(leaveId && { leaveId }),
  };
  const response = await apiRequest("POST", "/leaves/check-overlap", payload);
  return response.data;
};
